import {
  Avatar,
  AvatarColorOption,
  CollapseCard,
  FlexBox,
  IconNames,
  Text,
} from '@dixa/lib-shared-habla';
import { PropsWithChildren, ReactNode, useContext, useState } from 'react';

import { UserAuditLogEntry } from 'apis/auditLogs';
import { ConversationTimestamp } from 'components/conversation/components/conversationTimestamp/conversationTimestamp';

import { UserLink } from '../activityTypes/userLink/userLink';

import { CompactActivityEntry } from './compactActivityEntry';
import { DetailField, hasValue } from './detailField';
import styles from './userActivityCard.module.scss';
import { UserActivityLayoutContext } from './userActivityLayoutContext';

export type CardField = DetailField;

type UserActivityCardProps = {
  readonly entry: UserAuditLogEntry;
  /** The avatar icon for the event. */
  readonly icon: IconNames;
  /** The tinted-circle color for the avatar (see `statusColors`). */
  readonly color: AvatarColorOption;
  /** The card title, e.g. "Order created". */
  readonly title: string;
  /** Rendered inline right after the title, e.g. a linked conversation id. */
  readonly titleSuffix?: ReactNode;
  /** An at-a-glance summary shown under the title, visible without expanding. */
  readonly summary?: ReactNode;
  /** Labelled detail columns revealed when the card is expanded. */
  readonly fields?: readonly CardField[];
};

/**
 * A single Customer Activities entry, rendered as an expandable habla
 * `CollapseCard`: a tinted-circle avatar, title, timestamp and an at-a-glance
 * summary in the header, with the labelled detail columns and any line items
 * revealed on expand. The sidebar uses `CompactActivityEntry` instead.
 */
export const UserActivityCard = ({
  entry,
  icon,
  color,
  title,
  titleSuffix,
  summary,
  fields = [],
  children,
}: PropsWithChildren<UserActivityCardProps>): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);
  const isCompact = useContext(UserActivityLayoutContext) === 'compact';

  const timestamp = (
    <ConversationTimestamp
      shouldDateFormatPersist
      time={entry.event_timestamp}
      isSecondsDisplayed={false}
    />
  );
  const titleLine = (
    <span className={styles.titleLine}>
      <span>{title}</span>
      {titleSuffix != null && (
        <span className={styles.titleSuffix}>{titleSuffix}</span>
      )}
    </span>
  );
  const author = entry.author.name && entry.author.id !== entry.resource.id && (
    <Text.Paragraph
      as="span"
      variant="4"
      subdued
      className={isCompact ? undefined : styles.inlineMeta}
    >
      by{' '}
      <UserLink
        userId={entry.author.id}
        onClick={(event) => event.stopPropagation()}
      >
        {entry.author.name}
      </UserLink>
    </Text.Paragraph>
  );
  const summaryText = summary && (
    <Text.Paragraph as="span" variant="4" subdued className={styles.summary}>
      {summary}
    </Text.Paragraph>
  );
  const shownFields = fields.filter(hasValue);
  const expandable = shownFields.length > 0 || children != null;
  const details = (as: 'div' | 'li') => (
    <FlexBox
      as={as}
      flexDirection="column"
      gap="300"
      padding="300"
      margin="0 0 300 500"
      background="neutral.100"
      borderRadius="100"
      className={styles.panel}
    >
      {shownFields.length > 0 && (
        <div className={styles.fields}>
          {shownFields.map((field, index) => (
            <DetailField key={index} {...field} />
          ))}
        </div>
      )}
      {children}
    </FlexBox>
  );

  if (isCompact) {
    return (
      <CompactActivityEntry
        icon={icon}
        color={color}
        title={titleLine}
        timestamp={timestamp}
        author={author}
        summary={summaryText}
        details={expandable ? details('div') : undefined}
        isOpen={expandable && isOpen}
        onToggle={() => setIsOpen(!isOpen)}
      />
    );
  }

  return (
    <CollapseCard
      className={styles.card}
      isOpen={expandable && isOpen}
      onToggle={expandable ? setIsOpen : () => undefined}
    >
      <CollapseCard.Header
        className={styles.headerRow}
        avatar={
          <span className={styles.avatar}>
            <Avatar icon={icon} color={color} size="24" />
          </span>
        }
        title={title}
        wrap="normal"
        compact
        nonCollapsible={!expandable}
        slotAfterTitle={timestamp}
      >
        {titleLine}
        {author}
        {summaryText}
      </CollapseCard.Header>
      {expandable && (
        <CollapseCard.Content>{details('li')}</CollapseCard.Content>
      )}
    </CollapseCard>
  );
};
